/* Higuchi cart grouping: regular / preorder / catalog. Theme-side validation. */
(() => {
  'use strict';
  const MESSAGE = '即納商品と予約商品、または納期が異なる予約商品は、一緒にご注文いただけません。納期ごとに分けてご注文ください。\n無料カタログも、他の商品と一緒にお申し込みいただけません。';
  const UNAVAILABLE = 'カートの内容を確認できませんでした。時間をおいて、もう一度お試しください。';
  const mixed = (items) => new Set(items.filter((item) => item.quantity > 0).map((item) => item.group)).size > 1;
  const quantity = (value) => {
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < 0) throw new Error(UNAVAILABLE);
    return number;
  };
  function proposedCart(current, data, operation, lookup) {
    const next = current.map((item) => ({ ...item }));
    let increases = false;
    if (operation === 'add') {
      for (const item of data.items || [data]) {
        const amount = quantity(item.quantity ?? 1);
        if (!amount) continue;
        const known = current.find((line) => String(line.id) === String(item.id)) || lookup(item.id);
        if (!known) throw new Error(UNAVAILABLE);
        next.push({ id: item.id, group: known.group, quantity: amount });
        increases = true;
      }
    } else if (data.updates) {
      for (const [key, amount] of Object.entries(data.updates)) {
        const count = quantity(amount);
        const found = Array.isArray(data.updates) ? next[Number(key)] :
          next.find((line) => line.key === key) || next.find((line) => String(line.id) === key);
        if (found) {
          if (count > found.quantity) increases = true;
          found.quantity = count;
        } else if (count > 0) {
          const known = lookup(key);
          if (!known) throw new Error(UNAVAILABLE);
          next.push({ id: key, group: known.group, quantity: count });
          increases = true;
        }
      }
    }
    // Always permit reductions so an already mixed cart can be repaired.
    if (increases && mixed(next)) throw new Error(MESSAGE);
    return next;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { mixed, proposedCart, MESSAGE };
  if (typeof window === 'undefined') return;

  const nativeFetch = window.fetch.bind(window);
  let queue = Promise.resolve();
  let current = null;
  let stateError = false;
  let alertOpener = null;
  const activeForms = new WeakSet();
  const approvedForms = new WeakSet();
  const cartUrl = () => window.routes.cart_url;
  const enqueue = (task) => {
    const run = () => navigator.locks ? navigator.locks.request('higuchi-cart', task) : task();
    const result = queue.then(run, run);
    queue = result.catch(() => {});
    return result;
  };
  function showError(message) {
    const alert = document.getElementById('HiguchiCartAlert');
    if (!alert) { window.alert(message); return; }
    alertOpener = document.activeElement;
    alert.querySelector('[data-higuchi-alert-text]').textContent = message;
    alert.hidden = false;
    alert.focus();
  }
  function applyState() {
    const invalid = current === null || mixed(current);
    document.querySelectorAll('[data-higuchi-checkout]').forEach((element) => { element.hidden = invalid; });
    document.querySelectorAll('[data-higuchi-cart-message]').forEach((element) => {
      element.hidden = !stateError && (current === null || !mixed(current));
      const message = stateError ? UNAVAILABLE : MESSAGE;
      if (element.textContent !== message) element.textContent = message;
    });
  }
  async function readState() {
    const url = new URL(cartUrl(), location.origin);
    url.searchParams.set('section_id', 'higuchi-cart-state');
    url.searchParams.set('_higuchi', Date.now());
    const response = await nativeFetch(url, { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.timeout(10000) });
    if (!response.ok) throw new Error(UNAVAILABLE);
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const node = page.querySelector('[data-higuchi-cart-state]');
    if (!node) throw new Error(UNAVAILABLE);
    const items = JSON.parse(node.textContent);
    if (!Array.isArray(items) || items.some((item) => !(typeof item.group === 'string' && /^(regular|catalog|preorder:(delivery|variant):.+)$/.test(item.group)) || !Number.isInteger(item.quantity))) {
      throw new Error(UNAVAILABLE);
    }
    current = items;
    stateError = false;
    applyState();
    return items;
  }
  async function refresh() {
    try { return await readState(); }
    catch (error) { current = null; stateError = true; applyState(); throw error; }
  }
  function lookup(id) {
    for (const node of document.querySelectorAll('[data-higuchi-product]')) {
      const group = JSON.parse(node.dataset.variantGroups)[String(id)];
      if (typeof group === 'string') return { group };
    }
    return null;
  }
  function requestData(body) {
    if (typeof body === 'string') return JSON.parse(body);
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const data = Object.fromEntries(body.entries());
      const updates = {};
      for (const [key, value] of body.entries()) {
        const match = key.match(/^updates\[(.+)\]$/);
        if (match) updates[match[1]] = value;
      }
      if (Object.keys(updates).length) data.updates = updates;
      return data;
    }
    return body || {};
  }
  function rejection(error) {
    const message = error.message === MESSAGE ? MESSAGE : UNAVAILABLE;
    showError(message);
    return new Response(JSON.stringify({ status: 422, message, description: message, higuchi_cart_guard: true }), {
      status: 422, headers: { 'Content-Type': 'application/json' },
    });
  }
  async function guardedFetch(url, config = {}) {
    return enqueue(async () => {
      const operation = new URL(url, location.origin).pathname.match(/\/cart\/(add|update|change|clear)(?:\.js)?\/?$/)?.[1];
      if (operation === 'add' || operation === 'update') {
        try {
          const data = requestData(config.body);
          if (operation === 'add' || data.updates) proposedCart(await refresh(), data, operation, lookup);
        } catch (error) { return rejection(error); }
      }
      const response = await nativeFetch(url, config);
      if (operation && response.ok) {
        // Do not report an add failure after a successful mutation: a retry would add twice.
        await refresh().catch(() => {});
      }
      return response;
    });
  }
  async function addAndVisitCart(body, owner) {
    if (activeForms.has(owner)) return;
    activeForms.add(owner);
    owner.setAttribute('aria-busy', 'true');
    try {
      const url = window.routes.cart_add_url.replace(/(?:\.js)?$/, '.js');
      const response = await guardedFetch(url, { method: 'POST', body, credentials: 'same-origin' });
      const result = await response.json();
      if (!response.ok || result.status) {
        if (!result.higuchi_cart_guard) showError(result.description || result.message || UNAVAILABLE);
        return;
      }
      location.assign(cartUrl());
    } catch (error) { showError(UNAVAILABLE); }
    finally { activeForms.delete(owner); owner.removeAttribute('aria-busy'); }
  }
  function checkout(action) {
    enqueue(async () => {
      try {
        const items = await refresh();
        if (!items.length) return;
        if (mixed(items)) { showError(MESSAGE); return; }
        action();
      } catch (error) { showError(UNAVAILABLE); }
    });
  }
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (approvedForms.has(form)) { approvedForms.delete(form); return; }
    const path = new URL(form.action, location.origin).pathname;
    if (/\/cart\/add(?:\.js)?\/?$/.test(path)) {
      // Dawn's product-form keeps its own Ajax rendering and uses guardedFetch.
      if (form.closest('product-form')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      addAndVisitCart(new FormData(form), form);
    } else if (event.submitter?.name === 'checkout' || /\/checkout\/?$/.test(path)) {
      event.preventDefault(); event.stopImmediatePropagation();
      const submitter = event.submitter;
      checkout(() => {
        approvedForms.add(form);
        form.requestSubmit(submitter || undefined);
        approvedForms.delete(form);
      });
    }
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-higuchi-alert-close]')) {
      document.getElementById('HiguchiCartAlert').hidden = true;
      alertOpener?.focus();
      return;
    }
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin) return;
    if (/\/cart\/add(?:\.js)?\/?$/.test(url.pathname)) {
      event.preventDefault(); event.stopImmediatePropagation();
      addAndVisitCart(url.searchParams, link);
    } else if (/\/checkout\/?$/.test(url.pathname)) {
      event.preventDefault(); event.stopImmediatePropagation();
      checkout(() => location.assign(url.href));
    }
  }, true);
  window.HiguchiCartGuard = { fetch: guardedFetch, refresh: () => enqueue(refresh) };
  const initial = document.querySelector('[data-higuchi-cart-state]');
  try { if (initial) current = JSON.parse(initial.textContent); } catch (_) { /* refresh fails closed */ }
  applyState();
  new MutationObserver(applyState).observe(document.body, { childList: true, subtree: true });
  const scheduleRefresh = () => enqueue(refresh).catch(() => {});
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleRefresh);
    }
  });
  window.addEventListener('pageshow', scheduleRefresh);
  window.addEventListener('focus', scheduleRefresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(); });
})();
